/* eslint-disable no-nested-ternary */
import { FormattedMessage, useIntl } from 'react-intl';
import { AnimatePresence, LayoutGroup, motion, Variants } from 'framer-motion';
import * as React from 'react';

import { SurveyConfig } from '../types';

import useQuestionResult from '../hooks/useQuestionResult';
import useQuestionResults from '../hooks/useQuestionResults';
import useSubmitterUid from '../hooks/useSubmitterUid';
import useSurvey from '../hooks/useSurvey';

import SurveyColumns from '../components/SurveyColumns';
import SurveyContainer from '../components/SurveyContainer';
import SurveyCover from '../components/SurveyCover';
import SurveyNavButton from '../components/SurveyNavButton';
import SurveyResult from '../components/SurveyResult';
import SurveyResultContainer from '../components/SurveyResultContainer';
import SurveyStartContainer from '../components/SurveyStartContainer';
import Text from '../../../text/Text.jsx';

import StartTeaser from './StartTeaser';
import Question from './Question';

export type AppProps = {
  config: SurveyConfig;
};

type Screen = 'start' | 'question';

type Direction = 'prev' | 'next';

const questionVariant: Variants = {
  initial: (direction: Direction) => ({
    opacity: 0,
    x: direction === 'prev' ? '-100%' : '100%'
  }),
  visible: {
    opacity: 1,
    x: 0
  },
  exit: (direction: Direction) => ({
    opacity: 0,
    x: direction === 'prev' ? '100%' : '-100%'
  })
};

const containerVariant: Variants = {
  initial: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5
    }
  },
  exit: {
    opacity: 0
  }
};

const App = ({ config }: AppProps) => {
  const intl = useIntl();

  const [[screen, direction], setScreen] = React.useState<[Screen, Direction]>([
    'start',
    'next'
  ]);

  const submitterUid = useSubmitterUid();

  const {
    currentQuestion,
    currentQuestionIdx,
    hasNextQuestion,
    goToPrevQuestion,
    goToNextQuestion
  } = useSurvey(config);

  const {
    data: resultData,
    submitAnswer,
    reset: resetResult
  } = useQuestionResult(submitterUid, config.uid, currentQuestion.uid);

  const results = useQuestionResults(currentQuestion.uid, resultData);

  const onClickPrevQuestion = React.useCallback(() => {
    resetResult();
    setScreen(['question', 'prev']);
    goToPrevQuestion();
  }, [resetResult, setScreen, goToPrevQuestion]);

  const onClickNextQuestion = React.useCallback(() => {
    resetResult();
    setScreen(['question', 'next']);
    goToNextQuestion();
  }, [resetResult, setScreen, goToNextQuestion]);

  const onSubmitAnswer = React.useCallback(
    async (answerUid: string) => {
      await submitAnswer(answerUid);
      setScreen(['question', 'next']);
    },
    [submitAnswer, setScreen]
  );

  const questionResultData = React.useMemo(
    () => results[currentQuestion.uid],
    [results, currentQuestion]
  );

  if (screen === 'start') {
    return (
      <motion.div
        key="start"
        initial="initial"
        animate="visible"
        exit="exit"
        variants={containerVariant}
      >
        <SurveyStartContainer
          headline={<FormattedMessage id="startHeadline" />}
          left={
            config.startTeaser && (
              <StartTeaser
                formatSlideHeadline={(question) => (
                  <FormattedMessage
                    id="startSlideHeadline"
                    values={{ question }}
                  />
                )}
                nextButtonTitle={intl.formatMessage({
                  id: 'startSliderNextButtonLabel'
                })}
                prevButtonTitle={intl.formatMessage({
                  id: 'startSliderPrevButtonLabel'
                })}
                startTeaser={config.startTeaser}
                surveyUid={config.uid}
                topline={config.startTeaser.topline}
              />
            )
          }
          right={
            <SurveyCover
              headline={config.title}
              topline={config.topline}
              buttonLabel={<FormattedMessage id="startCtaLabel" />}
              onButtonClick={() => setScreen(['question', 'next'])}
              duration={config.readingTime}
            />
          }
        />
      </motion.div>
    );
  }

  let prevButton = null;
  let nextButton = null;
  let content = null;

  if (screen === 'question') {
    prevButton =
      currentQuestionIdx === 0 ? (
        <SurveyNavButton
          iconId="btn-prev"
          label={<FormattedMessage id="backButtonLabel" />}
          onClick={() => setScreen(['start', 'prev'])}
        />
      ) : (
        <SurveyNavButton
          iconId="btn-prev"
          label={<FormattedMessage id="prevQuestionButtonLabel" />}
          onClick={onClickPrevQuestion}
        />
      );

    nextButton = hasNextQuestion ? (
      <SurveyNavButton
        iconId="btn-next"
        label={<FormattedMessage id="nextQuestionButtonLabel" />}
        onClick={onClickNextQuestion}
      />
    ) : null;

    if (questionResultData) {
      content =
        !hasNextQuestion && config.endTeaser ? (
          <SurveyColumns
            columnConfig={['1 / span 5', '6 / span 4']}
            col1={
              <SurveyResultContainer
                topline={
                  <FormattedMessage
                    id="resultTopline"
                    values={{ question: currentQuestion.question }}
                  />
                }
                headline={<FormattedMessage id="resultHeadline" />}
              >
                <SurveyResult questionResults={questionResultData} />
              </SurveyResultContainer>
            }
            col2={
              <SurveyCover
                topline={config.endTeaser?.topline}
                headline={config.endTeaser?.title}
                buttonLabel={config.endTeaser?.action.label}
                onButtonClick={() => {
                  window.open(
                    config.endTeaser?.action.href,
                    config.endTeaser?.action.target || '_self'
                  );
                }}
              />
            }
          />
        ) : (
          <SurveyColumns
            columnConfig={['1 / span 3', '4 / span 5']}
            col1={<Text variant="copy">{currentQuestion.question}</Text>}
            col2={
              <SurveyResult
                headline={<FormattedMessage id="resultHeadline" />}
                questionResults={questionResultData}
              />
            }
          />
        );
    } else {
      content = (
        <Question
          currentQuestion={currentQuestion}
          formatHint={(isLoading, isValid) =>
            isLoading ? (
              <FormattedMessage id="questionLoading" />
            ) : (
              <FormattedMessage id="questionHint" values={{ isValid }} />
            )
          }
          onSubmit={onSubmitAnswer}
        />
      );
    }
  }

  return (
    <motion.div
      key="survey"
      initial="initial"
      animate="visible"
      exit="exit"
      variants={containerVariant}
    >
      <LayoutGroup>
        <SurveyContainer
          headline={<FormattedMessage id="questionsHeadline" />}
          prevButton={prevButton}
          nextButton={nextButton}
        >
          <AnimatePresence initial={false} mode="wait" custom={direction}>
            <motion.div
              key={[screen, !!questionResultData, currentQuestion.uid].join('')}
              initial="initial"
              animate="visible"
              exit="exit"
              custom={direction}
              variants={questionVariant}
            >
              {content}
            </motion.div>
          </AnimatePresence>
        </SurveyContainer>
      </LayoutGroup>
    </motion.div>
  );
};

export default App;
